import type { CSSProperties, DragEventHandler, EventHandler, MouseEventHandler, ReactNode, SyntheticEvent } from 'react'
import React from 'react'
import { Icon, Spinner } from '@ztechapps/webui.util'
import { Button } from 'react-bootstrap'
import type { ButtonVariant } from 'react-bootstrap/types'

export type BusyButtonProps = {
  isBusy?: boolean
  disabled?: boolean
  bsStyle?: string
  variant?: ButtonVariant
  bsSize?: 'sm' | 'lg'
  className?: string
  type?: 'button' | 'submit' | 'reset'
  onClick?: EventHandler<SyntheticEvent> | MouseEventHandler<HTMLButtonElement>
  icon?: string | ReactNode
  text?: string
  busyText?: string
  title?: string
  name?: string
  active?: boolean
  children?: ReactNode
  style?: CSSProperties | undefined
  ref?: any
  // Native HTML5 DnD passthrough — required so this button can serve as a
  // `useDragSort` handle. `draggable=true` placed on any parent does NOT
  // initiate a drag from inside a `<button>`; the button element itself
  // must carry `draggable` and own the drag-source handlers.
  // `draggable` is typed as the React `Booleanish` union (`boolean | 'true' | 'false'`)
  // so callers forwarding `ButtonProps` from react-bootstrap can spread cleanly.
  draggable?: boolean | 'true' | 'false'
  onDragStart?: DragEventHandler<HTMLButtonElement>
  onDragEnd?: DragEventHandler<HTMLButtonElement>
  'data-drag-sort-index'?: number
}

/**
 *  The common button that handles busy state
 */
export const BusyButton = ({
  children,
  isBusy = false,
  disabled = false,
  bsStyle = 'primary',
  variant,
  bsSize,
  className = '',
  type = 'button',
  onClick,
  icon,
  text,
  busyText = 'Loading...',
  title,
  active = false,
  style = undefined,
  ref,
  draggable,
  onDragStart,
  onDragEnd,
  'data-drag-sort-index': dataDragSortIndex,
}: BusyButtonProps) => {
  //check the value from the variant first, then try to use teh bsStyle
  bsStyle = variant ?? bsStyle

  //Update this to handle custom icons
  let renderIcon = null
  if (icon) {
    if (typeof icon === 'string') {
      renderIcon = <Icon name={icon} />
    } else renderIcon = icon
  }

  let renderText = null
  if (text) {
    //if busy and there is a value for busy text
    if (isBusy && busyText) {
      text = busyText
    }
    renderText = text
  }

  let buttonSpinner = null
  if (isBusy) {
    buttonSpinner = <Spinner size={'sm'} />
  }

  if (isBusy) {
    return (
      <Button size={bsSize} variant={bsStyle} className={className} type={type} disabled style={style}>
        {buttonSpinner} {busyText}
      </Button>
    )
  }
  if (disabled || isBusy) {
    className = `${className} disabled`
    onClick = () => {
      //empty here
    }
  }

  return (
    <Button
      ref={ref}
      title={title}
      size={bsSize}
      variant={bsStyle}
      className={className}
      type={type}
      onClick={onClick}
      disabled={disabled || isBusy}
      active={active}
      style={style}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      data-drag-sort-index={dataDragSortIndex}
    >
      {renderIcon} {renderText} {children}
    </Button>
  )
}
